import React, { useEffect, useMemo, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
    X, Plus,
    FileText, AlertTriangle, Shield, ClipboardCheck, Zap, ScrollText,
    Wrench, Briefcase, Tag,
} from 'lucide-react';
import FormControl from './FormControl';
import Button from './Button';
import {
    entityRegistryService, EntityType, EntityRecord,
} from '../../services/entityRegistryService';
import { TaskLink } from '../../services/tasksService';
// Multi-link field. Two-step picker (type → record) plus existing links as
// chips. Visual contract lives in EntityLinksMultiField.css so every caller
// (TaskForm, MeetingDetailModal agenda section, …) renders identically.

interface Props {
    value: TaskLink[];
    onChange: (next: TaskLink[]) => void;
    /** Restrict the type dropdown to this set of type_codes. */
    allowedTypes?: string[];
    disabled?: boolean;
}

interface ModuleTheme {
    icon: LucideIcon;
    bg: string;
    border: string;
    fg: string;
    accent: string;
}

const MODULE_THEMES: Record<string, ModuleTheme> = {
    GOV: { icon: FileText,        bg: '#eff6ff', border: '#bfdbfe', fg: '#1e40af', accent: '#3b82f6' },
    RSK: { icon: AlertTriangle,   bg: '#fef2f2', border: '#fecaca', fg: '#991b1b', accent: '#ef4444' },
    CTR: { icon: Shield,          bg: '#f5f3ff', border: '#ddd6fe', fg: '#5b21b6', accent: '#8b5cf6' },
    COM: { icon: ClipboardCheck,  bg: '#f0fdfa', border: '#99f6e4', fg: '#115e59', accent: '#14b8a6' },
    IRG: { icon: Zap,             bg: '#fffbeb', border: '#fde68a', fg: '#92400e', accent: '#f59e0b' },
    ADT: { icon: ScrollText,      bg: '#f8fafc', border: '#cbd5e1', fg: '#334155', accent: '#64748b' },
    CAP: { icon: Wrench,          bg: '#fff7ed', border: '#fed7aa', fg: '#9a3412', accent: '#f97316' },
    PRM: { icon: Briefcase,       bg: '#eef2ff', border: '#c7d2fe', fg: '#3730a3', accent: '#6366f1' },
};

const DEFAULT_THEME: ModuleTheme = {
    icon: Tag, bg: '#f3f4f6', border: '#d1d5db', fg: '#374151', accent: '#6b7280',
};

const themeFor = (moduleCode?: string | null): ModuleTheme =>
    (moduleCode && MODULE_THEMES[moduleCode]) || DEFAULT_THEME;

// Tag each type with its depth + parent's display_name so the dropdown can
// render the parent → child chain (e.g. "Audit Program → Audit Finding").
// Roots come in module-sort_order order; children appear immediately after
// their parent so the visual grouping is intact.
interface OrderedType extends EntityType {
    depth: number;
    parentDisplayName?: string;
}

function orderHierarchically(types: EntityType[]): OrderedType[] {
    const byCode = new Map<string, EntityType>();
    types.forEach(t => byCode.set(t.type_code, t));

    const childrenByParent = new Map<string, EntityType[]>();
    const roots: EntityType[] = [];
    types.forEach(t => {
        const parent = t.parent_type_code && byCode.has(t.parent_type_code)
            ? t.parent_type_code : null;
        if (!parent) {
            roots.push(t);
        } else {
            const arr = childrenByParent.get(parent) ?? [];
            arr.push(t);
            childrenByParent.set(parent, arr);
        }
    });

    const sortFn = (a: EntityType, b: EntityType) =>
        a.module_code.localeCompare(b.module_code)
        || (a.sort_order ?? 100) - (b.sort_order ?? 100)
        || a.display_name.localeCompare(b.display_name);

    const out: OrderedType[] = [];
    const walk = (t: EntityType, depth: number, parentName?: string) => {
        out.push({ ...t, depth, parentDisplayName: parentName });
        const kids = (childrenByParent.get(t.type_code) ?? []).sort(sortFn);
        kids.forEach(k => walk(k, depth + 1, t.display_name));
    };
    roots.sort(sortFn).forEach(r => walk(r, 0));
    return out;
}

const EntityLinksMultiField: React.FC<Props> = ({
    value, onChange, allowedTypes, disabled,
}) => {
    const [types, setTypes] = useState<OrderedType[]>([]);
    const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
    const [records, setRecords] = useState<EntityRecord[]>([]);
    const [selectedRecordId, setSelectedRecordId] = useState<number | null>(null);
    const [loadingRecords, setLoadingRecords] = useState(false);

    // Load types once.
    useEffect(() => {
        entityRegistryService.listTypes(true).then((all) => {
            const filtered = allowedTypes && allowedTypes.length
                ? all.filter(t => allowedTypes.includes(t.type_code))
                : all;
            setTypes(orderHierarchically(filtered));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowedTypes?.join(',')]);

    // When type changes, load records for that type.
    useEffect(() => {
        if (!selectedTypeId) { setRecords([]); setSelectedRecordId(null); return; }
        const t = types.find(x => x.id === selectedTypeId);
        if (!t) return;
        let alive = true;
        setLoadingRecords(true);
        setSelectedRecordId(null);
        // Backend caps /utl/lookup at limit=100. Anything higher 422s.
        entityRegistryService.lookup(t.type_code, undefined, undefined, 100)
            .then(data => { if (alive) setRecords(data); })
            .catch(err => {
                if (alive) {
                    setRecords([]);
                    // eslint-disable-next-line no-console
                    console.error('lookup failed', t.type_code, err);
                }
            })
            .finally(() => { if (alive) setLoadingRecords(false); });
        return () => { alive = false; };
    }, [selectedTypeId, types]);

    const typeByCode = useMemo(() => {
        const m = new Map<string, EntityType>();
        types.forEach(t => m.set(t.type_code, t));
        return m;
    }, [types]);

    const linkedKeys = useMemo(() => {
        const s = new Set<string>();
        value.forEach(v => s.add(`${v.type_id}:${v.record_id}`));
        return s;
    }, [value]);

    const typeOptions = useMemo(() =>
        types.map(t => ({
            value: t.id,
            // Show parent → child chain so hierarchy is visible in the flat
            // dropdown. Root types render plain; children render as
            // "Audit Program  ›  Audit Finding".
            label: t.depth > 0 && t.parentDisplayName
                ? `${t.parentDisplayName}  ›  ${t.display_name}`
                : t.display_name,
        })),
        [types],
    );

    const recordOptions = useMemo(() =>
        records.map(r => {
            const already = selectedTypeId
                ? linkedKeys.has(`${selectedTypeId}:${r.id}`) : false;
            const label = r.code ? `${r.code} — ${r.label}` : r.label;
            return {
                value: r.id,
                label: already ? `${label} (already linked)` : label,
                disabled: already,
            };
        }),
        [records, selectedTypeId, linkedKeys],
    );

    const handleAdd = () => {
        if (!selectedTypeId || !selectedRecordId) return;
        const t = types.find(x => x.id === selectedTypeId);
        const r = records.find(x => x.id === selectedRecordId);
        if (!t || !r) return;
        const key = `${selectedTypeId}:${selectedRecordId}`;
        if (linkedKeys.has(key)) return;
        const newLink: TaskLink = {
            type_id: selectedTypeId,
            type_code: t.type_code,
            record_id: selectedRecordId,
            label: r.label,
            code: r.code,
        };
        onChange([...value, newLink]);
        setSelectedRecordId(null);
    };

    const removeLink = (idx: number) => {
        onChange(value.filter((_, i) => i !== idx));
    };

    const renderChip = (link: TaskLink, idx: number) => {
        const tdef = link.type_code ? typeByCode.get(link.type_code) : undefined;
        // Per UAT 2026-05-17: drop the module-themed icon + colored chip
        // styling. A link is a link — neutral chip, type label as a small
        // pill, the entity code (mono) + label, and an X to remove. Cleaner
        // and lets the user scan the list quickly.
        return (
            <div
                key={`${link.type_id}-${link.record_id}-${idx}`}
                className="entity-link-field__chip"
            >
                <span className="entity-link-field__chip-type">
                    {tdef?.display_name || link.type_code || 'Item'}
                </span>
                {link.code ? (
                    <span className="entity-link-field__chip-code">
                        {link.code}
                    </span>
                ) : <span />}
                <span className="entity-link-field__chip-label">
                    {link.label || `#${link.record_id}`}
                </span>
                {!disabled ? (
                    <button
                        type="button"
                        onClick={() => removeLink(idx)}
                        title="Remove link"
                        aria-label={`Remove link to ${link.label || link.type_code}`}
                        className="entity-link-field__chip-remove"
                    >
                        <X size={14} />
                    </button>
                ) : <span />}
            </div>
        );
    };

    const canAdd = !!(selectedTypeId && selectedRecordId
        && !linkedKeys.has(`${selectedTypeId}:${selectedRecordId}`));

    // Step-aware copy. Vague placeholders ("Select type", "Pick a type
    // first") confused users who didn't know what "type" referred to in
    // this context. Renaming to "Choose a category" / "Pick the item"
    // surfaces the two-step nature of the picker.
    const recordPlaceholder = !selectedTypeId
        ? '— choose a category first —'
        : loadingRecords
            ? 'Loading items…'
            : records.length === 0
                ? 'No items found for this category'
                : 'Search and pick an item';

    return (
        <div className="entity-link-field">
            {/* Existing chips */}
            {value.length > 0 && (
                <div className="entity-link-field__chips">
                    {value.map(renderChip)}
                </div>
            )}

            {/* Picker row — only when not disabled. Per UAT 2026-05-17 the
                earlier "Step 1 / Step 2" badges + dashed panel were too
                heavy. The two SearchSelects now sit on a single row with
                no chrome; their placeholders carry the meaning. */}
            {!disabled && (
                <div className="entity-link-field__picker">
                    <FormControl.SearchSelect
                        options={typeOptions}
                        value={selectedTypeId}
                        onChange={v => setSelectedTypeId(v !== null ? Number(v) : null)}
                        placeholder="Policy / Risk / Control / …"
                        noMargin
                    />
                    <FormControl.SearchSelect
                        options={recordOptions}
                        value={selectedRecordId}
                        onChange={v => setSelectedRecordId(v !== null ? Number(v) : null)}
                        placeholder={recordPlaceholder}
                        disabled={!selectedTypeId || loadingRecords}
                        noMargin
                    />
                    <Button
                        variant="fill"
                        icon={Plus}
                        onClick={handleAdd}
                        disabled={!canAdd}
                    >
                        Create link
                    </Button>
                </div>
            )}

            {/* Empty hint — one light muted line. */}
            {value.length === 0 && (
                <div className="entity-link-field__empty">
                    {disabled ? 'No items linked.' : 'No items linked yet.'}
                </div>
            )}
        </div>
    );
};

export default EntityLinksMultiField;
